// Offline catch-up (TECHNICAL-ARCHITECTURE.md §2.3). Replays elapsed real time
// in coarse 15-sim-minute ticks when the player returns.
//
// The rule that matters: nobody comes back to a disaster. Stats floor at 20,
// illness can start but never worsens, and no predator visits while away.

import type { GameState, StoredEgg } from '../core/types';
import { getConfig, type Profile } from './config';
import { stepSimulation } from './simulate';

export interface AwayEmotion {
  key: string;
  params: Record<string, string | number>;
}

export interface CatchupSummary {
  /** Real hours that actually elapsed, before the cap. */
  elapsedHours: number;
  /** Hours simulated — capped at 48 (BIOLOGY-ENGINE grace rules). */
  simulatedHours: number;
  capped: boolean;
  eggsLaid: StoredEgg[];
  birdsThatGotSick: Array<{ chickenId: string; condition: string }>;
  choresNowDue: number;
  /** Milliseconds the replay took; the budget is 500ms for a 48h worst case. */
  durationMs: number;
  /** Named, emotional lines for the "While you were away" surface. */
  emotions: AwayEmotion[];
}

export interface CatchupOptions {
  now?: number;
  profile?: Profile;
}

/**
 * Replays the time between `lastSavedTimestamp` and now. Returns the data for
 * the "While you were away…" modal, which doubles as a teaching moment.
 */
export function runOfflineCatchup(
  state: GameState,
  options: CatchupOptions = {},
): CatchupSummary {
  const startedAt = Date.now();
  const now = options.now ?? Date.now();
  const profile: Profile = options.profile ?? (state.veteran.enabled ? 'veteran' : 'normal');
  const config = getConfig(profile);

  const elapsedMs = Math.max(0, now - state.lastSavedTimestamp);
  const elapsedRealHours = elapsedMs / 3_600_000;

  // Real-time only, so wall-clock time and sim time are the same thing: the
  // player was away for exactly as long as the clock says.
  const simHoursElapsed = elapsedRealHours;

  const cap = config.grace.offlineCapHours;
  const simulatedHours = Math.min(simHoursElapsed, cap);
  const capped = simHoursElapsed > cap;

  const summary: CatchupSummary = {
    elapsedHours: elapsedRealHours,
    simulatedHours,
    capped,
    eggsLaid: [],
    birdsThatGotSick: [],
    choresNowDue: 0,
    durationMs: 0,
    emotions: [],
  };

  if (simulatedHours <= 0) return summary;

  const stepHours = config.grace.offlineCoarseTickMinutes / 60;
  const steps = Math.ceil(simulatedHours / stepHours);

  for (let i = 0; i < steps; i++) {
    // The last step may be a partial one.
    const hours = Math.min(stepHours, simulatedHours - i * stepHours);
    const report = stepSimulation(state, { hours, offline: true, profile });
    for (const laid of report.eggsLaid) summary.eggsLaid.push(laid.egg);
    for (const condition of report.newConditions) {
      summary.birdsThatGotSick.push({
        chickenId: condition.chickenId,
        condition: condition.condition,
      });
    }
  }

  // Past the cap the flock is treated as having managed itself: no further
  // decay, and stats are lifted back to the grace floor.
  if (capped) {
    for (const chicken of state.flock) {
      const p = chicken.physiology;
      const floor = config.grace.minStatWhileAway;
      p.hydration = Math.max(p.hydration, floor);
      p.energyReserve = Math.max(p.energyReserve, floor);
    }
  }

  state.lastSavedTimestamp = now;
  summary.durationMs = Date.now() - startedAt;
  summary.emotions = buildAwayEmotions(state, summary);
  return summary;
}

/**
 * Turns the simulated gap into 1-3 named, warm lines for the return screen.
 * Pulls from real data: eggs laid, birds that got sick, birds whose thirst or
 * hunger ran low. No guilt, only attachment. Returns nothing when no time
 * passed or the flock is empty.
 */
export function buildAwayEmotions(
  state: GameState,
  summary: CatchupSummary,
): AwayEmotion[] {
  if (summary.simulatedHours <= 0 || state.flock.length === 0) return [];

  const lines: AwayEmotion[] = [];
  const named = (id: string): string =>
    state.flock.find((c) => c.id === id)?.name ?? '';

  // Eggs first: the happiest surprise. One line per layer, up to two named.
  const layers = new Map<string, number>();
  for (const egg of summary.eggsLaid) {
    layers.set(egg.layerId, (layers.get(egg.layerId) ?? 0) + 1);
  }
  for (const [layerId, count] of layers) {
    if (lines.length >= 2) break;
    const name = named(layerId);
    if (!name) continue;
    lines.push(
      count === 1
        ? { key: 'away.egg', params: { name } }
        : { key: 'away.eggs', params: { name, count } },
    );
  }

  // A bird that got sick: name it, name the attachment.
  for (const sick of summary.birdsThatGotSick) {
    if (lines.length >= 3) break;
    const name = named(sick.chickenId);
    if (!name) continue;
    lines.push({ key: 'away.sick', params: { name } });
  }

  // Thirst and hunger: find the neediest bird so the line lands with a name.
  let thirstyName: string | null = null;
  let thirstyValue = Infinity;
  let hungryName: string | null = null;
  let hungryValue = Infinity;
  for (const chicken of state.flock) {
    const p = chicken.physiology;
    if (p.hydration < thirstyValue) {
      thirstyValue = p.hydration;
      thirstyName = chicken.name;
    }
    if (p.energyReserve < hungryValue) {
      hungryValue = p.energyReserve;
      hungryName = chicken.name;
    }
  }
  // Threshold: below the "reduced" rung (40) counts as a real need.
  if (lines.length < 3 && thirstyName && thirstyValue < 40) {
    lines.push({ key: 'away.thirst', params: { name: thirstyName } });
  }
  if (lines.length < 3 && hungryName && hungryValue < 40) {
    lines.push({ key: 'away.hunger', params: { name: hungryName } });
  }

  // Fallback: nothing notable, still name a bird so it feels personal.
  if (lines.length === 0) {
    lines.push({ key: 'away.missedYou', params: { name: state.flock[0].name } });
  }

  return lines.slice(0, 3);
}
